import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { MarketOverviewLineChartLoading } from '@/pages/ReportCenter/MarketOverview/components/Shared';

/**
 * 每日消耗柱形图骨架
 */
export function BarChartSkeleton() {
  const barHeights = ['58%', '72%', '64%', '82%', '75%', '61%', '78%'];

  return (
    <div className="relative h-[150px] px-3 pb-5 pl-6">
      <div className="absolute inset-x-6 inset-y-2 flex flex-col justify-between pb-5">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-px w-full rounded-none" />
        ))}
      </div>
      <div className="relative flex h-full items-end gap-2 border-b border-l border-border px-2 pt-2">
        {barHeights.map((height, index) => (
          <Skeleton key={index} className="flex-1 rounded-b-none rounded-t-sm" style={{ height }} />
        ))}
      </div>
      <Skeleton className="absolute bottom-0 left-1/2 h-3 w-16 -translate-x-1/2" />
    </div>
  );
}

/**
 * 展示次数折线图骨架
 */
export function LineChartSkeleton() {
  const points = [
    [8, 72],
    [54, 58],
    [100, 68],
    [146, 38],
    [192, 52],
    [238, 76],
    [292, 44],
  ];

  return (
    <div className="relative h-[150px] px-3 pb-5 pl-6">
      <div className="absolute inset-x-6 inset-y-2 flex flex-col justify-between pb-5">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-px w-full rounded-none" />
        ))}
      </div>
      <div className="relative h-full border-b border-l border-border px-2 pt-2">
        <svg
          aria-hidden="true"
          className="h-full w-full animate-pulse text-accent"
          preserveAspectRatio="none"
          viewBox="0 0 300 110"
        >
          <polyline
            fill="none"
            points={points.map(([x, y]) => `${x},${y}`).join(' ')}
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="5"
          />
          {points.map(([x, y], index) => (
            <circle key={index} cx={x} cy={y} fill="currentColor" r="6" />
          ))}
        </svg>
      </div>
      <Skeleton className="absolute bottom-0 left-1/2 h-3 w-20 -translate-x-1/2" />
    </div>
  );
}

/**
 * 顶部指标卡片骨架
 */
export function MetricCardsSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 4 }, (_, index) => (
        <Card key={index}>
          <CardHeader>
            <Skeleton className="h-4 w-20" />
          </CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-8 w-28" />
            <Skeleton className="h-3 w-20" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * 最近创编表格骨架
 */
export function RecentCampaignTableSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 4 }, (_, rowIndex) => (
        <div key={rowIndex} className="grid grid-cols-9 gap-4">
          {Array.from({ length: 9 }, (_, columnIndex) => (
            <Skeleton key={columnIndex} className="h-5 w-full" />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Figma 工作台统一骨架
 * 整个首屏加载阶段保持同一结构，避免角色接口分步返回时切换骨架造成闪烁
 */
function UnifiedWorkbenchSkeleton() {
  return (
    <div data-testid="workbench-skeleton" className="flex flex-col gap-6">
      {/** 问候、昨日消耗摘要或角色经营简报 */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <Skeleton className="h-[29px] w-52" />
          <Skeleton className="h-5 w-12 rounded-[4px]" />
        </div>
        <Skeleton className="h-5 w-80" />
      </div>

      {/** 角色快捷操作 */}
      <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton
            key={index}
            data-testid="quick-action-skeleton"
            className="h-12 w-full rounded-xl"
          />
        ))}
      </div>

      {/** 三张今日指标卡 */}
      <div className="grid gap-6 md:grid-cols-3">
        {Array.from({ length: 3 }, (_, index) => (
          <section
            key={index}
            data-testid="metric-card-skeleton"
            className="flex h-[126px] min-w-0 flex-col gap-2.5 rounded-xl border bg-card p-4"
          >
            <div className="flex min-w-0 flex-col gap-6">
              <Skeleton className="h-[17px] w-24" />
              <Skeleton className="h-[29px] w-28" />
            </div>
            <Skeleton className="h-[14px] w-24" />
          </section>
        ))}
      </div>

      {/** 两张趋势图 */}
      <div className="grid gap-6 xl:grid-cols-2">
        {Array.from({ length: 2 }, (_, index) => (
          <section
            key={index}
            data-testid="chart-card-skeleton"
            className="h-[368px] min-w-0 rounded-xl border bg-card px-6 py-5"
          >
            <div className="flex items-center justify-between gap-4">
              <Skeleton className="h-5 w-28" />
              <Skeleton className="h-9 w-[88px]" />
            </div>
            <div className="mt-4 h-[244px]">
              <MarketOverviewLineChartLoading />
            </div>
            <div className="mt-2 flex items-center justify-center gap-5">
              <Skeleton className="h-3 w-14" />
              {index === 1 ? <Skeleton className="h-3 w-16" /> : null}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

/**
 * 工作台路由及首屏数据加载骨架
 * 路由加载和两组接口请求期间均使用同一结构，只在数据完整后切换真实页面
 */
export function WorkbenchSkeleton() {
  return <UnifiedWorkbenchSkeleton />;
}
